<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import {
  Activity,
  AudioLines,
  Banknote,
  BookOpenCheck,
  ChartNoAxesCombined,
  ChevronLeft,
  Library,
  Menu,
  Music2,
  PenLine,
  Rocket,
  Settings,
  Sparkles,
  Users,
  X,
} from "@lucide/vue";

const route = useRoute();
const collapsed = ref(false);
const mobileOpen = ref(false);
const isPublic = computed(() => Boolean(route.meta.public));

const nav = [
  {
    label: "创作",
    items: [
      { to: "/", label: "经营总览", icon: Activity },
      { to: "/create", label: "AI 创作", icon: Sparkles },
      { to: "/lyrics", label: "歌词工作台", icon: PenLine },
      { to: "/library", label: "作品曲库", icon: Library },
    ],
  },
  {
    label: "发行与增长",
    items: [
      { to: "/release", label: "发行中心", icon: Rocket },
      { to: "/analytics", label: "播放分析", icon: ChartNoAxesCombined },
      { to: "/revenue", label: "收益实验室", icon: Banknote },
      { to: "/growth", label: "增长任务", icon: AudioLines },
    ],
  },
  {
    label: "工作室",
    items: [
      { to: "/rights", label: "版权与合规", icon: BookOpenCheck },
      { to: "/mobile-users", label: "申请列表", icon: Users },
      { to: "/settings", label: "设置", icon: Settings },
    ],
  },
];

const pageDetails: Record<string, { description: string; theme: string }> = {
  "/": { description: "掌握创作进度、发行状态与听众信号", theme: "overview" },
  "/create": { description: "把灵感制作成可发行的高音质母带", theme: "create" },
  "/lyrics": { description: "从主题、结构到副歌记忆点的写作空间", theme: "lyrics" },
  "/library": { description: "管理你的每一首母带与公开试听链接", theme: "library" },
  "/release": { description: "整理元数据、封面与网易云发行资料", theme: "release" },
  "/analytics": { description: "读取播放趋势与作品的早期听众信号", theme: "analytics" },
  "/revenue": { description: "把播放目标、成本和回本路径算清楚", theme: "revenue" },
  "/growth": { description: "为每首新歌建立可执行的首发节奏", theme: "growth" },
  "/rights": { description: "在发行前完成素材、署名与版权自检", theme: "rights" },
  "/mobile-users": { description: "审核移动端申请、调整每日额度并查看生成记录", theme: "settings" },
  "/settings": { description: "管理创作引擎、艺人身份与发行通道", theme: "settings" },
};

const currentNavItem = computed(() =>
  nav.flatMap((group) => group.items).find((item) => item.to === route.path),
);
const pageDetail = computed(
  () => pageDetails[route.path] || pageDetails["/"],
);
</script>

<template>
  <router-view v-if="isPublic" />

  <div
    v-else
    class="app-shell"
    :class="[
      { 'sidebar-collapsed': collapsed },
      `route-${pageDetail.theme}`,
    ]"
  >
    <aside class="sidebar" :class="{ open: mobileOpen }">
      <div class="sidebar-aurora" aria-hidden="true" />
      <div class="brand-row">
        <router-link class="brand" to="/" @click="mobileOpen = false">
          <span class="brand-mark"><Music2 :size="20" /></span>
          <span class="brand-copy">
            <strong>声浪工坊</strong>
            <small>WAVECRAFT</small>
          </span>
        </router-link>
        <button class="icon-button close-mobile" aria-label="关闭菜单" @click="mobileOpen = false">
          <X :size="19" />
        </button>
      </div>

      <nav class="side-nav" aria-label="主导航">
        <section v-for="group in nav" :key="group.label">
          <p class="nav-group-label">{{ group.label }}</p>
          <router-link
            v-for="item in group.items"
            :key="item.to"
            :to="item.to"
            class="nav-item"
            @click="mobileOpen = false"
          >
            <span class="nav-icon">
              <component :is="item.icon" :size="17" :stroke-width="1.8" />
            </span>
            <span>{{ item.label }}</span>
          </router-link>
        </section>
      </nav>

      <div class="sidebar-foot">
        <div class="model-chip">
          <span class="live-dot" />
          <span>
            <small>生成引擎</small>
            <strong>MiniMax Music 3.0</strong>
          </span>
        </div>
        <button class="collapse-button" @click="collapsed = !collapsed">
          <ChevronLeft :size="17" />
          <span>收起导航</span>
        </button>
      </div>
    </aside>

    <div class="mobile-scrim" :class="{ show: mobileOpen }" @click="mobileOpen = false" />

    <main class="main-shell">
      <header class="topbar" :class="`topbar-${pageDetail.theme}`">
        <div class="topbar-art" aria-hidden="true">
          <span v-for="height in [22, 42, 31, 58, 78, 46, 68, 37, 52, 28]" :key="height" :style="{ height: `${height}%` }" />
        </div>
        <div class="topbar-left">
          <button class="icon-button menu-mobile" aria-label="打开菜单" @click="mobileOpen = true">
            <Menu :size="20" />
          </button>
          <span class="page-symbol">
            <component :is="currentNavItem?.icon || Activity" :size="21" :stroke-width="1.7" />
          </span>
          <div>
            <p class="eyebrow">{{ route.meta.kicker }}</p>
            <h1>{{ route.meta.title }}</h1>
            <p class="topbar-description">{{ pageDetail.description }}</p>
          </div>
        </div>
        <div class="topbar-actions">
          <div class="quota-pill" title="实际额度以 MiniMax 控制台为准">
            <span class="quota-ring">—</span>
            <span><strong>Token Plan</strong><small>额度以控制台为准</small></span>
          </div>
          <router-link class="button button-dark button-sm" to="/create">
            <Sparkles :size="16" />
            开始创作
          </router-link>
        </div>
      </header>

      <div class="page-container">
        <router-view />
      </div>
    </main>
  </div>
</template>
